/*!
 * CHIEF - Cybersecurity Learning Platform (PROPRIETARY SOFTWARE)
 * Theme Configuration
 * 
 * Copyright (c) 2025 Srivatsan Sk. All rights reserved.
 * 
 * PROPRIETARY LICENSE NOTICE:
 * This software is the exclusive proprietary property of Srivatsan Sk.
 * Licensed to client under strict commercial license agreement.
 * Unauthorized use, copying, modification, distribution, or removal
 * of copyright notices is strictly prohibited.
 * 
 * Original Author: Srivatsan Sk
 * License: Proprietary/Commercial
 * Status: Confidential
 * 
 * WARNING: This code contains proprietary business logic and algorithms.
 * Reverse engineering, decompilation, or derivative works are prohibited.
 */

// src/styles/theme.js
import { createGlobalStyle } from 'styled-components';

export const theme = {
    colors: {
        // Core colors
        background: '#0F0F13',
        backgroundLight: '#1A1A20',
        backgroundLighter: '#24242C',
        primary: '#0AFF70',
        primaryDark: '#06C058',
        secondary: '#09B6FF',
        secondaryDark: '#0788C0',
        accent: '#FF2E63',

        // Text
    text: '#E0E0E7',
        textPrimary: '#E0E0E7',
        textSecondary: '#8A8A9D',
        textMuted: '#55556A',

        // Status
        success: '#00CC66',
        warning: '#FFB82E',
        error: '#FF3A5E',
        info: '#4C83FF',

        // Gradients
        gradientPrimary: 'linear-gradient(135deg, #0AFF70 0%, #09B6FF 100%)',
        gradientDark: 'linear-gradient(135deg, #06C058 0%, #0788C0 100%)',

        // Special
        terminal: '#121218',
        glow: 'rgba(10, 255, 112, 0.3)',
        divider: 'rgba(255, 255, 255, 0.08)',
        cardGlow: 'rgba(10, 255, 112, 0.05)',

        // Difficulty
        beginner: '#00CC66',
        intermediate: '#FFB82E',
        advanced: '#FF3A5E',
        expert: '#D740FF'
    },

    shadows: {
        subtle: '0 4px 8px rgba(0, 0, 0, 0.15)',
        medium: '0 6px 12px rgba(0, 0, 0, 0.2)',
        strong: '0 8px 24px rgba(0, 0, 0, 0.3)',
        glow: '0 0 20px rgba(10, 255, 112, 0.15)',
        cardHover: '0 10px 25px rgba(10, 255, 112, 0.07)'
    },

    fonts: {
        primary: "'Rajdhani', sans-serif",
        code: "'Fira Code', monospace",
        sizes: {
            xs: '0.75rem',
            sm: '0.875rem',
            md: '1rem',
            lg: '1.125rem',
            xl: '1.25rem',
            '2xl': '1.5rem',
            '3xl': '1.875rem',
            '4xl': '2.25rem',
            '5xl': '3rem'
        }
    },

    borderRadius: {
        xs: '2px',
        sm: '4px',
        md: '6px',
        lg: '8px',
        xl: '12px',
        '2xl': '16px',
        full: '9999px'
    },

    spacing: {
        '0': '0',
        '1': '0.25rem',
        '2': '0.5rem',
        '3': '0.75rem',
        '4': '1rem',
        '5': '1.25rem',
        '6': '1.5rem',
        '8': '2rem',
        '10': '2.5rem',
        '12': '3rem',
        '16': '4rem',
        '20': '5rem',
        '24': '6rem',
        '32': '8rem',
        '40': '10rem',
        '48': '12rem',
        '56': '14rem',
        '64': '16rem'
    },

    transitions: {
        fast: '0.15s ease',
        normal: '0.3s ease',
        slow: '0.5s ease'
    },

    zIndex: {
        base: 0,
        above: 10,
        dropdown: 100,
        sticky: 200,
        fixed: 300,
        modal: 400,
        popover: 500,
        tooltip: 600
    }
};

export const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@300;400;500;600;700&display=swap');

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body {
    height: 100%;
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 16px;
    line-height: 1.5;
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textPrimary};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }

  #root {
    height: 100%;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitions.fast};
    
    &:hover {
      color: ${({ theme }) => theme.colors.secondary};
    }
  }

  button {
    font-family: ${({ theme }) => theme.fonts.primary};
    cursor: pointer;
    border: none;
  }

  button, input, select, textarea {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  input, select, textarea {
    border: 1px solid ${({ theme }) => theme.colors.backgroundLighter};
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    padding: 0.75rem 1rem;
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.glow};
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  ::selection {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
  }
  
  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.backgroundLighter};
    border-radius: 3px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textMuted};
  }
  
  .loading-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.primary};
  }
  
  .loader {
    border: 4px solid rgba(10, 255, 112, 0.1);
    border-radius: 50%;
    border-top: 4px solid ${({ theme }) => theme.colors.primary};
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
